import React, { useMemo } from 'react';
import {
  SignalCompany,
  SignalContactSummary,
  SignalDateGroup,
  MarketSignalDateGroup,
  WebsiteSignalDateGroup,
  LinkedInSignalDateGroup,
} from '@/types/signals';
import { ListResponse } from '@/types/common';
import Icon from '../ui/Icon';
import FilterButton from '@/app/components/ui/FilterButton';
import { CustomScroll } from '../ui/CustomScroll';
import SignalActivityItem from './SignalActivityItem';
import WebsiteSignalActivityItem from './WebsiteSignalActivityItem';
import WebsiteSignalActivityPopover from './WebsiteSignalActivityPopover';
import { cn, formatDateGroup } from '@/app/lib/utils';
import CompanyMiniCard from '../common/CompanyMiniCard';
import ContactMiniCard from '../common/ContactMiniCard';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getCompanySignals,
  getContactMarketSignals,
  getContactLinkedInSignals,
  getContactWebsiteSignals,
} from '@/api/signals';
import {
  getSalesforceMarketSignals,
  getSalesforceLinkedInSignals,
  getSalesforceWebsiteSignals,
} from '@/api/salesforce-embed';
import { useSalesforceEmbed } from '@/app/context/salesforce-embed';
import Spinner from '../ui/Spinner';
import StatusCard from '../ui/StatusCard';
import { useParams } from 'next/navigation';
import useFilter from '@/app/hooks/use-filter';
import Modal from '../ui/Modal';
import Filter, { FilterField } from '../common/Filter';
import { useTranslations } from 'next-intl';
import { getSignalGroups } from './signalGroups';
import { Globe, Linkedin, Megaphone, Radio, Timer } from 'lucide-react';
import { useContactDetailsDrawer } from '@/app/context/contact-details-drawer';
import SignalDetailsFilterPopover, {
  type SignalDetailsFilter,
  type SignalDetailsFilterSummary,
} from './SignalDetailsFilterPopover';
import QueryFilterSummaryChips from '../query-builder/QueryFilterSummaryChips';
import type { QueryValue } from '../query-builder/types';
import MarketSignalActivityItem from './MarketSignalActivityItem';
import LinkedInSignalActivityItem from './LinkedInSignalActivityItem';
import { signalTextClassName } from '@/constants/color';

const formatDuration = (seconds: number) => {
  if (!seconds || seconds < 1) return '';
  const rounded = Math.round(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remainingSeconds = rounded % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

interface ContactHeaderData {
  id: string;
  name: string;
  linkedin?: string;
  title?: string;
  profile_pic: string;
  persona?: {
    id?: string;
    short_name?: string;
    reason?: string;
  };
}

interface SignalDetailsProps {
  visitor: SignalCompany | SignalContactSummary | null;
  isFetching?: boolean;
  contactHeader?: ContactHeaderData | null;
  detailType?: 'company' | 'contact';
  companyHeader?: {
    id: string;
    name: string;
    logo: string;
    linkedin?: string;
    qualification_reason?: string;
  } | null;
  showContactOpenButton?: boolean;
  hideContactHeader?: boolean;
  hideFilters?: boolean;
  signalType?: 'website' | 'market' | 'linkedin';
  onFilterOpenChange?: (open: boolean) => void;
}

const SignalDetails: React.FC<SignalDetailsProps> = ({
  visitor,
  isFetching: isRefreshing,
  contactHeader,
  detailType = 'company',
  companyHeader,
  showContactOpenButton = true,
  hideContactHeader = false,
  hideFilters = false,
  signalType = 'website',
  onFilterOpenChange,
}) => {
  const { clientId } = useParams<{ clientId?: string }>();
  const salesforceEmbed = useSalesforceEmbed();
  const isFilterHidden = hideFilters || salesforceEmbed.isEmbed;
  const resolvedClientId =
    clientId ||
    salesforceEmbed.clientId ||
    (visitor && 'client_id' in visitor ? visitor.client_id : '');
  const contactDetailsDrawer = useContactDetailsDrawer();
  const t = useTranslations('signals');
  const tCommon = useTranslations('common');
  const [isFilterModalOpen, setIsFilterModalOpen] = React.useState(false);
  const [detailsFilterState, setDetailsFilterState] = React.useState<{
    visitorId: string;
    filters: SignalDetailsFilter;
  }>({ visitorId: '', filters: { match: 'all', filters: [] } });
  const [detailsFilterSummaryState, setDetailsFilterSummaryState] = React.useState<{
    visitorId: string;
    summaries: SignalDetailsFilterSummary[];
  }>({ visitorId: '', summaries: [] });
  const removeDetailsFilterRef = React.useRef<(ruleId: string) => void>(() => {});
  const updateDetailsFilterValueRef = React.useRef<(ruleId: string, value: QueryValue) => void>(
    () => {}
  );
  const detailsFilters =
    detailsFilterState.visitorId === visitor?.id
      ? detailsFilterState.filters
      : { match: 'all' as const, filters: [] };
  const serializedDetailsFilters = JSON.stringify(detailsFilters);
  const detailsFilterSummaries =
    detailsFilterSummaryState.visitorId === visitor?.id ? detailsFilterSummaryState.summaries : [];
  const handleDetailsFiltersChange = React.useCallback(
    (filters: SignalDetailsFilter) => {
      setDetailsFilterState({ visitorId: visitor?.id || '', filters });
    },
    [visitor?.id]
  );
  const handleDetailsFilterSummaryChange = React.useCallback(
    (
      summaries: SignalDetailsFilterSummary[],
      remove: (ruleId: string) => void,
      updateValue: (ruleId: string, value: QueryValue) => void
    ) => {
      setDetailsFilterSummaryState({ visitorId: visitor?.id || '', summaries });
      removeDetailsFilterRef.current = remove;
      updateDetailsFilterValueRef.current = updateValue;
    },
    [visitor?.id]
  );
  const entityOptions = [
    { id: 'linkedin', value: t('entity.linkedin') },
    { id: 'email', value: t('entity.email') },
    { id: 'form', value: t('entity.form') },
    { id: 'page', value: t('entity.page') },
    { id: 'response', value: t('entity.response') },
    ...(detailType === 'contact' ? [] : [{ id: 'icp', value: t('entity.icp') }]),
    { id: 'persona', value: t('entity.persona') },
  ];

  const filterKeys =
    detailType === 'contact'
      ? ['sd_action', 'sd_entity', 'sd_signal_at_start']
      : ['sd_contact_id', 'sd_action', 'sd_entity', 'sd_signal_at_start'];

  const { filterSearchParams, setFilter, setAdditionalData, selectedFilterUi } = useFilter(
    filterKeys,
    {
      sd_contact_id: {
        label: t('filters.contactPerson'),
        type: 'contact-select',
      },
      sd_entity: {
        label: t('filters.entity'),
        type: 'select',
        options: entityOptions,
      },
      sd_action: {
        label: t('filters.action'),
        type: 'select',
        options: [
          { id: 'connected', value: t('actions.connected') },
          { id: 'opened', value: t('actions.opened') },
          { id: 'visited_page', value: t('actions.visited_page') },
          { id: 'received', value: t('actions.received') },
          { id: 'evaluated', value: t('actions.evaluated') },
        ],
      },
      sd_signal_at_start: {
        label: t('filters.dateRange'),
        icon: 'calendar-fill',
        type: 'daterange',
        keys: ['sd_signal_at_start', 'sd_signal_at_end'],
      },
    }
  );
  const hasActiveDetailsFilters =
    detailType === 'contact'
      ? detailsFilters.filters.length > 0 || (detailsFilters.targetPages?.length ?? 0) > 0
      : filterSearchParams.size > 0;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<
    ListResponse<
      SignalDateGroup | WebsiteSignalDateGroup | MarketSignalDateGroup | LinkedInSignalDateGroup
    >
  >({
    queryKey: [
      `${detailType}-signals`,
      resolvedClientId,
      visitor?.id,
      signalType,
      detailType === 'contact' ? serializedDetailsFilters : filterSearchParams.toString(),
      salesforceEmbed.isEmbed,
    ],
    queryFn: ({ pageParam = '' }) =>
      detailType === 'contact' && signalType === 'market'
        ? salesforceEmbed.isEmbed && salesforceEmbed.token
          ? getSalesforceMarketSignals({
              clientId: resolvedClientId || '',
              contactId: visitor?.id || '',
              token: salesforceEmbed.token,
              startingAfter: pageParam as string,
              match: detailsFilters.match,
              filters: detailsFilters.filters,
            })
          : getContactMarketSignals({
              clientId: resolvedClientId || '',
              contactId: visitor?.id || '',
              startingAfter: pageParam as string,
              match: detailsFilters.match,
              filters: detailsFilters.filters,
            })
        : detailType === 'contact' && signalType === 'linkedin'
          ? salesforceEmbed.isEmbed && salesforceEmbed.token
            ? getSalesforceLinkedInSignals({
                clientId: resolvedClientId || '',
                contactId: visitor?.id || '',
                token: salesforceEmbed.token,
                startingAfter: pageParam as string,
                match: detailsFilters.match,
                filters: detailsFilters.filters,
              })
            : getContactLinkedInSignals({
                clientId: resolvedClientId || '',
                contactId: visitor?.id || '',
                startingAfter: pageParam as string,
                match: detailsFilters.match,
                filters: detailsFilters.filters,
              })
          : detailType === 'contact'
            ? salesforceEmbed.isEmbed && salesforceEmbed.token
              ? getSalesforceWebsiteSignals({
                  clientId: resolvedClientId || '',
                  contactId: visitor?.id || '',
                  token: salesforceEmbed.token,
                  startingAfter: pageParam as string,
                  match: detailsFilters.match,
                  filters: detailsFilters.filters,
                })
              : getContactWebsiteSignals({
                  clientId: resolvedClientId || '',
                  contactId: visitor?.id || '',
                  startingAfter: pageParam as string,
                  match: detailsFilters.match,
                  filters: detailsFilters.filters,
                  targetPages: detailsFilters.targetPages,
                })
            : getCompanySignals({
                clientId: resolvedClientId || '',
                companyId: visitor?.id || '',
                startingAfter: pageParam as string,
                filterSearchParams,
              }),
    getNextPageParam: lastPage =>
      lastPage.starting_after && lastPage.starting_after !== ''
        ? lastPage.starting_after
        : undefined,
    initialPageParam: '',
    enabled: !!resolvedClientId && !!visitor?.id,
    refetchOnMount: false,
    gcTime: 0,
  });

  const dateGroups = useMemo<SignalDateGroup[]>(() => {
    if (!data || detailType === 'contact') return [];
    return data.pages.flatMap(page => page.data || []);
  }, [data, detailType]) as SignalDateGroup[];

  const websiteDateGroups = useMemo<WebsiteSignalDateGroup[]>(() => {
    if (!data || detailType !== 'contact' || signalType !== 'website') return [];
    return data.pages.flatMap(page => page.data || []) as WebsiteSignalDateGroup[];
  }, [data, detailType, signalType]);

  const marketDateGroups = useMemo<MarketSignalDateGroup[]>(() => {
    if (!data || detailType !== 'contact' || signalType !== 'market') return [];
    return data.pages.flatMap(page => page.data || []) as MarketSignalDateGroup[];
  }, [data, detailType, signalType]);

  const linkedInDateGroups = useMemo<LinkedInSignalDateGroup[]>(() => {
    if (!data || detailType !== 'contact' || signalType !== 'linkedin') return [];
    return data.pages.flatMap(page => page.data || []) as LinkedInSignalDateGroup[];
  }, [data, detailType, signalType]);

  if (!visitor) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground bg-background h-full border-l border-border">
        <div className="flex flex-col items-center gap-2">
          <Icon name="search-line" size={10} />
          <p>Select a signal to view details</p>
        </div>
      </div>
    );
  }

  if (isRefreshing) {
    return (
      <div className="relative flex-1 flex items-center justify-center bg-background h-full min-h-50 border-l border-border">
        <Spinner size={12} />
      </div>
    );
  }

  const resolvedCompanyHeader =
    companyHeader ||
    (detailType === 'company'
      ? {
          id: visitor.id,
          name:
            (visitor as SignalCompany).name?.short ||
            (visitor as SignalCompany).name?.full ||
            'Company',
          logo: (visitor as SignalCompany).logo,
          linkedin: (visitor as SignalCompany).social?.linkedin,
          qualification_reason: (visitor as SignalCompany).qualification_reason,
        }
      : null);

  const companyName = resolvedCompanyHeader?.name || 'Company';
  const companyLogo = resolvedCompanyHeader?.logo || '';
  const contactFilterField: FilterField = {
    name: 'sd_contact_id',
    label: t('filters.contactPerson'),
    type: 'contact-select',
    searchable: true,
    filterParams: { company_id: visitor.id },
  };
  const detailFilterFields: FilterField[] = [
    ...(detailType === 'contact' ? [] : [contactFilterField]),
    {
      name: 'sd_entity',
      label: t('filters.entity'),
      type: 'select',
      options: entityOptions,
    },
    {
      name: 'sd_action',
      label: t('filters.action'),
      type: 'select',
      options: [
        { id: 'connected', value: t('actions.connected') },
        { id: 'opened', value: t('actions.opened') },
        { id: 'visited_page', value: t('actions.visited_page') },
        { id: 'received', value: t('actions.received') },
        { id: 'evaluated', value: t('actions.evaluated') },
      ],
    },
    {
      name: 'sd_signal_at_start',
      label: t('filters.dateRange'),
      type: 'daterange',
      searchable: false,
      keys: ['sd_signal_at_start', 'sd_signal_at_end'],
    },
  ];

  return (
    <div className="flex flex-col h-full bg-background w-full relative">
      <div className="py-2.5 px-3 border-b border-border group">
        {contactHeader && detailType === 'contact' ? (
          <div className="flex w-full items-center gap-3">
            {!hideContactHeader && (
              <ContactMiniCard
                clickable={false}
                showArrow={showContactOpenButton}
                alwaysShowArrow={showContactOpenButton}
                wrapperClassName="min-w-0 flex-1 pr-1"
                contact={{
                  id: contactHeader.id,
                  name: contactHeader.name,
                  linkedin: contactHeader.linkedin,
                  title: contactHeader.title,
                  profile_pic: contactHeader.profile_pic,
                }}
                titleSuffix={
                  resolvedCompanyHeader ? (
                    <CompanyMiniCard
                      avatarSize={16}
                      avatarImageSize={16}
                      initialsClassName="text-[8px]"
                      wrapperClassName="min-w-0 shrink gap-1"
                      company={{
                        id: resolvedCompanyHeader.id,
                        name: companyName,
                        logo: companyLogo,
                      }}
                    />
                  ) : null
                }
              />
            )}
            {hideContactHeader && (
              <div className="flex flex-1 items-center gap-1 text-sm font-medium text-primary">
                <Radio size={16} strokeWidth={1.5} />
                {data?.pages[0]?.total !== undefined
                  ? t('signalItem.signalCount', { count: data.pages[0].total })
                  : '...'}
              </div>
            )}
            {!isFilterHidden && (
              <SignalDetailsFilterPopover
                key={`${visitor?.id}-${signalType}`}
                clientId={clientId || ''}
                signalType={signalType}
                onChange={handleDetailsFiltersChange}
                onSummaryChange={handleDetailsFilterSummaryChange}
                onOpenChange={onFilterOpenChange}
              />
            )}
          </div>
        ) : (
          <>
            {contactHeader && (
              <div className="pb-2.5 mb-2.5 border-b border-border group w-full">
                <ContactMiniCard
                  clickable={false}
                  showArrow={showContactOpenButton}
                  alwaysShowArrow={showContactOpenButton}
                  wrapperClassName="pr-1"
                  contact={{
                    id: contactHeader.id,
                    name: contactHeader.name,
                    linkedin: contactHeader.linkedin,
                    title: contactHeader.title,
                    profile_pic: contactHeader.profile_pic,
                  }}
                />
              </div>
            )}
            <CompanyMiniCard
              avatarSize={contactHeader ? 16 : undefined}
              avatarImageSize={contactHeader ? 16 : undefined}
              initialsClassName={contactHeader ? 'text-[8px]' : undefined}
              company={{
                id: resolvedCompanyHeader?.id || '',
                name: companyName,
                logo: companyLogo,
                linkedin: resolvedCompanyHeader?.linkedin,
              }}
            />
          </>
        )}
      </div>

      {!isFilterHidden && detailType === 'contact' && detailsFilterSummaries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
          <QueryFilterSummaryChips
            summaries={detailsFilterSummaries}
            onRemove={ruleId => removeDetailsFilterRef.current(ruleId)}
            onValueChange={(ruleId, value) => updateDetailsFilterValueRef.current(ruleId, value)}
          />
        </div>
      )}

      {detailType !== 'contact' && (
        <div className="flex flex-col gap-2 border-b border-border">
          <div className="py-[5px] px-3 flex justify-between items-center">
            <div className="text-[14px] font-medium text-muted-foreground flex items-center gap-1">
              <Radio size={16} strokeWidth={1.5} className="text-information-base" />
              {data?.pages[0]?.total !== undefined
                ? t('signalItem.signalCount', { count: data.pages[0].total })
                : '...'}
            </div>
            <FilterButton
              count={filterSearchParams.size}
              onClick={() => setIsFilterModalOpen(true)}
            />
          </div>
          {selectedFilterUi && <div className="px-3 pb-2">{selectedFilterUi}</div>}
        </div>
      )}

      <div className="flex-1 overflow-hidden relative">
        <CustomScroll
          className="h-full"
          viewportClassName="px-3"
          infiniteScroll={true}
          onLoadMore={() => fetchNextPage()}
          hasMore={hasNextPage}
          loading={isFetchingNextPage}
          showScrollToTop={true}
          scrollToTopThreshold={200}
        >
          <div className="py-0 pb-2">
            {detailType === 'contact' &&
            signalType === 'linkedin' &&
            linkedInDateGroups.length > 0 ? (
              linkedInDateGroups.map(dateGroup => (
                <div
                  key={dateGroup.date}
                  className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3 pt-2"
                >
                  <div className="flex size-5 items-center justify-center">
                    <Linkedin
                      size={16}
                      className={cn('self-center', signalTextClassName.linkedin)}
                    />
                  </div>
                  <div className="flex min-w-0 items-center gap-2 text-sm text-secondary-foreground">
                    <span>{formatDateGroup(dateGroup.date)}</span>
                    <span className="font-medium text-primary">
                      {t('signalItem.compactSignalCount', { count: dateGroup.total })}
                    </span>
                  </div>
                  <div className="flex justify-center pt-2">
                    <div className="h-full w-px bg-border" />
                  </div>
                  <div className="min-w-0 pb-3 pt-2">
                    <div className="overflow-hidden rounded-[8px] border border-border bg-secondary">
                      {dateGroup.signals.map(signal => (
                        <LinkedInSignalActivityItem key={signal.id} signal={signal} />
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : detailType === 'contact' &&
              signalType === 'market' &&
              marketDateGroups.length > 0 ? (
              marketDateGroups.map(dateGroup => (
                <div
                  key={dateGroup.date}
                  className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3 pt-2"
                >
                  <div className="flex size-5 items-center justify-center">
                    <Megaphone
                      size={16}
                      className={cn('self-center', signalTextClassName.market)}
                    />
                  </div>
                  <div className="flex min-w-0 items-center gap-2 text-sm text-secondary-foreground">
                    <span>{formatDateGroup(dateGroup.date)}</span>
                    <span className="font-medium text-primary">
                      {t('signalItem.compactSignalCount', { count: dateGroup.total })}
                    </span>
                  </div>
                  <div className="flex justify-center pt-2">
                    <div className="h-full w-px bg-border" />
                  </div>
                  <div className="min-w-0 pb-3 pt-2">
                    <div className="overflow-hidden rounded-[8px] border border-border bg-secondary">
                      {dateGroup.signals.map(signal => (
                        <MarketSignalActivityItem key={signal.id} signal={signal} />
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : detailType === 'contact' && websiteDateGroups.length > 0 ? (
              websiteDateGroups.map(dateGroup => {
                const totalDuration = dateGroup.signals.reduce(
                  (total, signal) => total + signal.website.session.duration_secs,
                  0
                );

                return (
                  <div
                    key={dateGroup.date}
                    className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-3 pt-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <Globe size={16} className={cn('self-center', signalTextClassName.website)} />
                    </div>
                    <div className="flex min-w-0 items-center gap-2 text-sm text-secondary-foreground">
                      <span>{formatDateGroup(dateGroup.date)}</span>
                      <span className="font-medium text-primary">
                        {dateGroup.total} {dateGroup.total === 1 ? 'signal' : 'signals'}
                      </span>
                      {totalDuration > 0 && (
                        <span className="flex items-center gap-0.5 font-medium text-muted-foreground">
                          <Timer size={12} strokeWidth={1.5} />
                          {formatDuration(totalDuration)}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-center pt-2">
                      <div className="h-full w-px bg-border" />
                    </div>
                    <div className="min-w-0 pt-2 pb-3">
                      <WebsiteSignalActivityPopover
                        disabled={salesforceEmbed.isEmbed}
                        dateGroup={dateGroup}
                        header={close =>
                          contactHeader ? (
                            <ContactMiniCard
                              clickable={false}
                              showArrow={showContactOpenButton}
                              alwaysShowArrow={showContactOpenButton}
                              onOpen={() => {
                                if (!showContactOpenButton) return;
                                close();
                                contactDetailsDrawer?.openContactDetails(contactHeader.id);
                              }}
                              contact={{
                                id: contactHeader.id,
                                name: contactHeader.name,
                                linkedin: contactHeader.linkedin,
                                title: contactHeader.title,
                                profile_pic: contactHeader.profile_pic,
                              }}
                              titleSuffix={
                                resolvedCompanyHeader ? (
                                  <CompanyMiniCard
                                    avatarSize={16}
                                    avatarImageSize={16}
                                    initialsClassName="text-[8px]"
                                    wrapperClassName="min-w-0 shrink gap-1"
                                    company={{
                                      id: resolvedCompanyHeader.id,
                                      name: companyName,
                                      logo: companyLogo,
                                    }}
                                  />
                                ) : null
                              }
                            />
                          ) : null
                        }
                      >
                        <div className="overflow-hidden rounded-[8px] border border-border bg-secondary">
                          {dateGroup.signals.map(signal => (
                            <WebsiteSignalActivityItem key={signal.id} signal={signal} />
                          ))}
                        </div>
                      </WebsiteSignalActivityPopover>
                    </div>
                  </div>
                );
              })
            ) : detailType !== 'contact' && dateGroups.length > 0 ? (
              dateGroups.map(dateGroup => (
                <div key={dateGroup.date} className="flex flex-col first:pt-0 pt-3">
                  <div className="flex items-center justify-center relative pt-2 pb-4 sticky top-0 bg-background z-10">
                    <div className="absolute left-0 right-0 h-px bg-border top-1/2 -translate-y-1/2" />
                    <div className="flex gap-2 items-center bg-background px-2 relative z-10 border border-border h-6 rounded-full text-[12px] font-medium text-muted-foreground">
                      {formatDateGroup(dateGroup.date)}
                      <div className="w-px h-2 bg-border" />
                      <div className="flex items-center text-[14px] text-foreground gap-1">
                        <Radio size={16} strokeWidth={1.5} className="text-information-base" />
                        {dateGroup.total}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-[26px]">
                    {getSignalGroups(dateGroup).map((group, groupIdx) => (
                      <SignalActivityItem
                        key={`${dateGroup.date}-${groupIdx}`}
                        group={group}
                        company={{
                          name: companyName,
                          logo: companyLogo,
                          linkedin: resolvedCompanyHeader?.linkedin || '',
                          qualification_reason: resolvedCompanyHeader?.qualification_reason,
                        }}
                        showContactTooltip={true}
                        showFullDate={false}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : isLoading ? (
              <div className="relative flex min-h-50 items-center justify-center">
                <Spinner size={8} />
              </div>
            ) : (
              <div className="flex min-h-50 items-center justify-center">
                <StatusCard
                  type="empty"
                  heading={hasActiveDetailsFilters ? t('noSignalsToShow') : 'No signals found'}
                />
              </div>
            )}
          </div>
        </CustomScroll>
      </div>
      <Modal
        showClose
        isOpen={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        title={tCommon('filter')}
      >
        <Filter
          layout="vertical"
          wrapperClassName="px-4 pt-4"
          fields={detailFilterFields}
          onSubmit={(values, additionalData) => {
            if (detailType !== 'contact') {
              setFilter('sd_contact_id', values['sd_contact_id']);
            }
            setFilter('sd_action', values['sd_action']);
            setFilter('sd_entity', values['sd_entity']);
            if (values['sd_signal_at_start']) {
              setFilter('sd_signal_at_start', values['sd_signal_at_start']);
            }
            setAdditionalData(additionalData || {});
            setIsFilterModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default SignalDetails;
